/**
 * Portal Sidebar Component
 * Collapsible navigation sidebar with project list
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Zap, 
  Home, 
  FolderOpen, 
  Settings, 
  ChevronLeft,
  ChevronRight,
  Plus,
  FolderKanban
} from 'lucide-react';
import { projectsApi } from '../../lib/api';

interface PortalSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface NavItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path: string;
}

interface Project {
  id: string;
  code: string;
  name: string;
  updatedAt: string;
}

const mainNav: NavItem[] = [
  { icon: Home, label: 'Dashboard', path: '/' },
  { icon: FolderOpen, label: 'Projects', path: '/projects' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

export function PortalSidebar({ collapsed, onToggle }: PortalSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);

  useEffect(() => {
    loadRecentProjects();
  }, [location.pathname]); // Reload when navigation happens

  // Listen for project updates (create/delete)
  useEffect(() => {
    const handleProjectsUpdated = () => {
      loadRecentProjects();
    };
    window.addEventListener('projects-updated', handleProjectsUpdated);
    return () => window.removeEventListener('projects-updated', handleProjectsUpdated);
  }, []);

  const loadRecentProjects = async () => {
    try {
      const response = await projectsApi.list({ limit: 5 });
      const projects = response.data?.data;  // data is array directly, not data.projects
      
      if (!projects || !Array.isArray(projects)) {
        setRecentProjects([]);
        return;
      }
      
      // Sort by updatedAt descending
      const sorted = [...projects].sort((a, b) => 
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setRecentProjects(sorted.slice(0, 5));
    } catch (error) {
      console.error('Failed to load recent projects:', error);
      setRecentProjects([]);
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const isProjectActive = (projectId: string) => {
    return location.pathname.includes(`/projects/${projectId}`);
  };

  return (
    <aside 
      className={`bg-white dark:bg-slate-900 text-slate-800 dark:text-white flex flex-col transition-all duration-300 border-r border-slate-200 dark:border-transparent ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className="p-4 flex items-center gap-3 border-b border-slate-200 dark:border-slate-700/50">
        <div className="w-10 h-10 bg-blue-600 dark:bg-blue-600/80 rounded-xl flex items-center justify-center flex-shrink-0 border border-blue-500 dark:border-blue-500/50">
          <Zap className="w-6 h-6 text-white" />
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <h1 className="font-mono font-bold text-lg leading-tight tracking-wide">
              <span className="text-slate-800 dark:text-slate-100">GRID</span>
              <span className="text-blue-600 dark:text-blue-400">INSPECT</span>
            </h1>
            <p className="text-[10px] font-mono text-slate-400 dark:text-slate-500 tracking-wider">// PORTAL v3.0</p>
          </div>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 p-3 space-y-1">
        {mainNav.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                active 
                  ? 'bg-blue-50 dark:bg-slate-800/80 text-blue-700 dark:text-slate-100 border border-blue-200 dark:border-slate-600/50' 
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
              title={collapsed ? item.label : undefined}
            >
              <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-blue-600 dark:text-blue-400' : ''}`} />
              {!collapsed && <span className="font-mono font-medium text-sm tracking-wide">{item.label.toUpperCase()}</span>}
            </Link>
          );
        })}

        {/* Projects Section */}
        {!collapsed && recentProjects.length > 0 && (
          <div className="pt-4 mt-4 border-t border-slate-200 dark:border-slate-700/50">
            <div className="flex items-center justify-between px-3 mb-2">
              <span className="text-[10px] font-mono font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                // Recent
              </span>
            </div>
            <div className="space-y-1">
              {recentProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    isProjectActive(project.id)
                      ? 'bg-blue-50 dark:bg-slate-800/80 text-blue-700 dark:text-slate-100 border border-blue-200 dark:border-slate-600/50'
                      : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
                >
                  <FolderKanban className={`w-4 h-4 flex-shrink-0 ${isProjectActive(project.id) ? 'text-blue-600 dark:text-blue-400' : ''}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono font-medium truncate">{project.code}</p>
                    <p className="text-[10px] font-mono text-slate-400 dark:text-slate-600 truncate">{project.name}</p>
                  </div>
                </Link>
              ))}
            </div>
            
            {/* New Project Button */}
            <button
              onClick={() => navigate('/projects')}
              className="w-full flex items-center gap-2 px-3 py-2 mt-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/50 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="text-xs font-mono">New Project</span>
            </button>
          </div>
        )}
      </nav>

      {/* Collapse Toggle */}
      <button
        onClick={onToggle}
        className="p-3 border-t border-slate-200 dark:border-slate-700/50 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors"
        title={collapsed ? 'Expand' : 'Collapse'}
      >
        {collapsed ? (
          <ChevronRight className="w-5 h-5" />
        ) : (
          <ChevronLeft className="w-5 h-5" />
        )}
      </button>
    </aside>
  );
}
